//@flow
import * as React from 'react';
import * as Immutable from 'immutable';

import { PaginatedReactTable } from 'common/components/core/table/PaginatedTable';

import { SizeEnum } from 'common/enums/UIEnums';

import type { MasterTerritory } from 'common/models/internal/MasterTerritoryPlan';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';

import { useTranslation, type TFunction } from 'react-i18next';

import theme from './TerritoryTable.scss';
import { themr } from 'react-css-themr';

type Props = {
    t: TFunction,
    data: Immutable.List<MasterTerritory>
};

const DEFAULT_COLUMNS: Array<EntityColumnType> = Object.freeze([
    {
        headerTextId: 'masterTerritoryTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: false,
        frozen: false,
        sortable: false
    },
    {
        headerTextId: 'masterTerritoryTable.code',
        accessor: 'code',
        frozen: false,
        infixSearchable: false,
        sortable: false
    },
    {
        headerTextId: 'masterTerritoryTable.name',
        accessor: 'name',
        sortable: false
    },
    {
        headerTextId: 'masterTerritoryTable.count',
        accessor: 'metrics.count',
        sortable: false
    },
    {
        headerTextId: 'masterTerritoryTable.totalRevenue',
        accessor: 'metrics.totalRevenue',
        sortable: false
    },
    {
        headerTextId: 'masterTerritoryTable.totalTAT',
        accessor: 'metrics.totalTAT',
        sortable: false
    }
]);

const TerritoryTable = (props: Props): React.Node => {
    const { t } = useTranslation('entityManagement');

    const _getColumns = React.useCallback(() => {
        return DEFAULT_COLUMNS.map((rawCol: Object) => {
            const { headerTextId } = rawCol;
            let column = rawCol;
            if (headerTextId) {
                column = {
                    ...rawCol,
                    headerText: t(headerTextId)
                };
            }
            return column;
        }).filter(column => column);
    }, [t]);

    const _getTdProps = React.useCallback(
        (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const { t } = props;
            return {
                t
            };
        },
        [props]
    );

    return (
        <PaginatedReactTable
            theme={theme}
            data={props.data.toArray()}
            totalPages={null}
            noDataText={t('noRowsFound')}
            columns={_getColumns()}
            defaultPageSize={null}
            getTdProps={_getTdProps}
            stickyHeader={false}
            loaderSize={SizeEnum.Small}
        />
    );
};

export default themr(null, theme)(TerritoryTable);
